import { type ScheduledActivityEntity } from 'clients/CallhomeApiTypes';
import { type NewActivityType } from 'components/ScheduledActivityForm/useScheduledEventForm';
import React from 'react';
import useSWR from 'swr';

export function calculateMonthlyUsage(
  spotCheck: Pick<
    NewActivityType,
    'startsAt' | 'endsAt' | 'reoccurringCount' | 'rule' | 'cameraIds'
  >,
  id: string,
): Promise<number> {
  return new Promise((resolve, reject) => {
    const worker = new Worker(new URL('../imageUsageWorker', import.meta.url), {
      type: 'module',
    });

    worker.postMessage({
      id,
      spotCheck,
      cameraIds: spotCheck.cameraIds.length,
      timeZone: 'America/Toronto',
    });

    worker.onmessage = (event) => {
      const { usage } = event.data;
      resolve(usage);
      worker.terminate();
    };

    worker.onerror = (error) => {
      reject(error);
      worker.terminate();
    };
  });
}

export function useScheduledSpotCheckMonthlyUsage(
  spotCheck:
    | Pick<
        ScheduledActivityEntity,
        'startsAt' | 'endsAt' | 'reoccurringCount' | 'rule' | 'cameraIds'
      >
    | undefined,
) {
  const id = React.useId();

  const spotCheckUsageKey = spotCheck
    ? [
        spotCheck.startsAt,
        spotCheck.endsAt,
        spotCheck.reoccurringCount,
        spotCheck.rule,
        spotCheck.cameraIds.length,
      ]
    : null;

  const {
    data: monthlyUsage,
    error,
    isLoading,
  } = useSWR(spotCheckUsageKey, spotCheck ? () => calculateMonthlyUsage(spotCheck, id) : null, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
  });

  return {
    monthlyUsage: monthlyUsage ?? null,
    loading: isLoading,
    error,
  };
}

export function useMultiScheduledSpotCheckMonthlyUsages(spotChecks: ScheduledActivityEntity[]) {
  const spotChecksKey = React.useMemo(
    () =>
      spotChecks.map((spotCheck) => ({
        id: spotCheck.id,
        startsAt: spotCheck.startsAt,
        endsAt: spotCheck.endsAt,
        reoccurringCount: spotCheck.reoccurringCount,
        rule: spotCheck.rule,
        cameraCount: spotCheck.cameraIds.length,
      })),
    [spotChecks],
  );

  const { data, isLoading } = useSWR(
    spotChecksKey,
    async () => {
      const promises = spotChecks.map(async (spotCheck) => {
        const usage = await calculateMonthlyUsage(spotCheck, spotCheck.id);
        return { id: spotCheck.id, usage };
      });

      const results = await Promise.all(promises);

      return results.reduce<Record<string, number>>((acc, { id, usage }) => {
        acc[id] = usage;
        return acc;
      }, {});
    },
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  );

  const monthlyUsages = React.useMemo(() => {
    if (isLoading) {
      return spotChecks.reduce<Record<string, number | 'Calculating'>>((acc, spotCheck) => {
        acc[spotCheck.id] = 'Calculating';
        return acc;
      }, {});
    }

    return data || {};
  }, [data, isLoading, spotChecks]);

  return { monthlyUsages, loading: isLoading };
}
